/**
 * The Czech maltsters: Raven Trading (Záhlinice) and Tchecomalt (Sladovny
 * Soufflet ČR, five maltings across Moravia and Bohemia).
 *
 * Both publish colour in EBC as a band, so `colorEbc` is the mid-point of the
 * published range and the °L figure alongside it is the conversion
 * `°L = (EBC + 1.2) / 2.65` rounded to one decimal, not a second measurement.
 * Neither publishes distilled-water mash pH or titratable buffering, so those
 * fields are `null` and the {@link MaltClass} defaults apply.
 *
 * ## Why the provenance splits the way it does
 *
 * **Raven publishes colour and nothing else.** Their floor-and-specialty malt
 * brochure gives an EBC band per product and no extract, moisture or diastatic
 * power at all, so every Raven row carries class-typical figures for those
 * three and is `representative`. Retail listings quote numbers Raven does not
 * — one US shop has the pilsen at 100–120 °Lintner, which is a Windisch-Kolbach
 * figure wearing the wrong unit — and a shop's arithmetic is not a maltster's
 * spec sheet, so none of it is used here.
 *
 * **Tchecomalt publishes colour, moisture and extract, but no diastatic
 * power.** Their crystal and roasted rows are therefore `spec-sheet`: every
 * stored number is theirs, and the zero diastatic power is physics the whole
 * catalog enforces rather than a figure anyone guessed. The base and kilned
 * rows need a real enzyme number that Tchecomalt does not print, so they carry
 * a class-typical one and are `representative`.
 *
 * Moisture and extract are quoted by Tchecomalt as limits (`max. 5 %`,
 * `min. 80.5 %`) rather than bands. The limit is stored as-is: it is the number
 * the maltster stands behind, and inventing a midpoint for a one-sided bound
 * would be exactly the invented precision the provenance rule exists to stop.
 *
 * Neither house publishes a per-product page — Raven has one brochure PDF for
 * the whole line and Tchecomalt one catalogue PDF — so no row carries a `url`.
 * A link to a family document is not a link to a product.
 */

import type { Fermentable } from '../../schema/fermentable';

/** Raven Trading and Tchecomalt malts. @see Fermentable */
export const czechFermentables: readonly Fermentable[] = [
  // -------------------------------------------------------------------------
  // Raven Trading — malthouse Záhlinice, floor malting since 1899.
  // -------------------------------------------------------------------------
  {
    id: 'raven-pilsen',
    name: 'Pilsen Malt',
    maker: 'Raven Trading',
    origin: 'Czech Republic',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 80.5,
    moisturePercent: 4.5,
    colorLovibond: 1.6,
    colorEbc: 3,
    diastaticPowerLintner: 70,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Haná-grown spring barley germinated on the floors at Záhlinice and kilned pale. The slow, unforced germination is what brewers buy it for: a rounder, bready malt spine under a světlý ležák than a pneumatic pilsner malt gives. Sold in the US as Czech Bohemian Pilsner.',
    tags: ['base', 'lager', 'pilsner', 'czech', 'bohemian', 'floor-malted'],
    provenance: 'representative',
  },
  {
    id: 'raven-pale-ale',
    name: 'Pale Ale Malt',
    maker: 'Raven Trading',
    origin: 'Czech Republic',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 80,
    moisturePercent: 4.5,
    colorLovibond: 2.3,
    colorEbc: 5,
    diastaticPowerLintner: 62,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Two-row barley kilned warmer than the pilsen malt, which costs it some enzyme and a little nitrogen and buys a fuller, faintly biscuity base. Raven point it at pale ale and at the paler end of stout and porter grists.',
    tags: ['base', 'ale', 'pale-ale', 'czech', 'floor-malted'],
    provenance: 'representative',
  },
  {
    id: 'raven-vienna',
    name: 'Vienna Malt',
    maker: 'Raven Trading',
    origin: 'Czech Republic',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 80,
    moisturePercent: 4.5,
    colorLovibond: 3.7,
    colorEbc: 8.5,
    diastaticPowerLintner: 65,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Roughly twice the color of the pilsen malt and sitting on the line between pale and dark, with the Maillard products a hotter kiln brings. Bready and golden enough to carry a polotmavý ležák or a Märzen on its own.',
    tags: ['base', 'kilned', 'vienna', 'lager', 'czech', 'floor-malted'],
    provenance: 'representative',
  },
  {
    id: 'raven-munich',
    name: 'Munich Malt',
    maker: 'Raven Trading',
    origin: 'Czech Republic',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 79,
    moisturePercent: 4.5,
    colorLovibond: 8,
    colorEbc: 20,
    diastaticPowerLintner: 42,
    suggestedMaxPercent: 80,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Finished at high kiln temperatures for aroma rather than color, so it gives up extract and enzyme in exchange for depth. The workhorse behind a tmavé pivo, and a large minority of a dark or festive lager grist.',
    tags: ['base', 'kilned', 'munich', 'dark', 'czech', 'floor-malted'],
    provenance: 'representative',
  },
  {
    id: 'raven-melanoidin',
    name: 'Melanoidin Malt',
    maker: 'Raven Trading',
    origin: 'Czech Republic',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 78,
    moisturePercent: 4.5,
    colorLovibond: 26.9,
    colorEbc: 70,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Color and malt depth driven all the way by the Maillard reaction rather than by the roaster, so it arrives without the bitter edge a roasted malt carries. A few percent deepens a bock or a dark wheat beer and firms up its malt aroma.',
    tags: ['kilned', 'melanoidin', 'malty', 'dark', 'czech'],
    provenance: 'representative',
  },
  {
    id: 'raven-wheat',
    name: 'Wheat Malt',
    maker: 'Raven Trading',
    origin: 'Czech Republic',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 83,
    moisturePercent: 5.5,
    colorLovibond: 2,
    colorEbc: 4,
    diastaticPowerLintner: 76,
    suggestedMaxPercent: 70,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Floor-malted wheat: more protein and polysaccharide than barley malt and no husk to build a filter bed from, so a big share of it wants rice hulls under the mash. Pays that back in head and in the soft, bready body of a wheat beer.',
    tags: ['base', 'wheat', 'czech', 'floor-malted'],
    provenance: 'representative',
  },
  {
    id: 'raven-caramel-extra-pale',
    name: 'Caramel Extra Pale',
    maker: 'Raven Trading',
    origin: 'Czech Republic',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 77,
    moisturePercent: 6,
    colorLovibond: 19.3,
    colorEbc: 50,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Deeply germinated green malt taken through the roaster at the gentlest setting Raven use. Adds body, head retention and a little redox protection to a pale beer without moving its color far.',
    tags: ['crystal', 'caramel', 'light', 'head-retention', 'body', 'czech'],
    provenance: 'representative',
  },
  {
    id: 'raven-caramel-pale',
    name: 'Caramel Pale',
    maker: 'Raven Trading',
    origin: 'Czech Republic',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 76,
    moisturePercent: 6,
    colorLovibond: 42,
    colorEbc: 110,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      "The middle of Raven's pale caramel pair: light toffee and a golden-amber cast, still short of the raisin and burnt-sugar notes the darker grades bring. Comfortable at a few percent in an amber lager or a Czech polotmavý.",
    tags: ['crystal', 'caramel', 'amber', 'malty', 'czech'],
    provenance: 'representative',
  },
  {
    id: 'raven-caramel-standard',
    name: 'Caramel Standard',
    maker: 'Raven Trading',
    origin: 'Czech Republic',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75,
    moisturePercent: 6,
    colorLovibond: 70.3,
    colorEbc: 185,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Darker husks over a glassy endosperm somewhere between yellow and brown. Clean malt aroma and a frank sweetness on the palate, which is what a tmavé ležák wants under its roast.',
    tags: ['crystal', 'caramel', 'dark', 'malty', 'czech'],
    provenance: 'representative',
  },
  {
    id: 'raven-caramel-dark',
    name: 'Caramel Dark',
    maker: 'Raven Trading',
    origin: 'Czech Republic',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 74,
    moisturePercent: 5,
    colorLovibond: 132.5,
    colorEbc: 350,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The end of the caramel range, aromatic and pushed far enough that the sweetness turns almost bitter. Small additions carry a dark lager or a stout a long way; a heavy hand makes the beer taste stewed.',
    tags: ['crystal', 'caramel', 'dark', 'stout', 'czech'],
    provenance: 'representative',
  },
  {
    id: 'raven-black',
    name: 'Black Malt',
    maker: 'Raven Trading',
    origin: 'Czech Republic',
    type: 'roasted',
    maltClass: 'roasted',
    // Raven publish 800–1500 EBC, and the roasted barley below is 1100–1200.
    // The two mid-points landing on the same number is the arithmetic, not a
    // copied row — Raven's black band is simply wide enough to straddle it.
    extractFineDryPercent: 72,
    moisturePercent: 4.5,
    colorLovibond: 434.4,
    colorEbc: 1150,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 3,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Kilned malt re-wetted and taken up a temperature ramp in the roasting drum, for color a Munich malt could never reach. Harsh and bitter with it, which is why Raven put the working dose at one to two percent of the grist.',
    tags: ['roasted', 'black', 'dark', 'stout', 'porter', 'czech'],
    provenance: 'representative',
  },
  {
    id: 'raven-roasted-rye',
    name: 'Roasted Rye',
    maker: 'Raven Trading',
    origin: 'Czech Republic',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 70,
    moisturePercent: 4.5,
    colorLovibond: 245.7,
    colorEbc: 650,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Unmalted rye taken straight to a high roast. Bitterness that reads as measured rather than sharp, over the grainy, faintly cereal note rye keeps through the drum. A different route into a stout than roasted barley.',
    tags: ['roasted', 'rye', 'dark', 'stout', 'czech'],
    provenance: 'representative',
  },
  {
    id: 'raven-roasted-barley',
    name: 'Roasted Barley',
    maker: 'Raven Trading',
    origin: 'Czech Republic',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 68,
    moisturePercent: 4.5,
    colorLovibond: 434.4,
    colorEbc: 1150,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Two-row spring barley roasted unmalted, so there is no sweetness anywhere in it. Sharp, dry and rough — the astringent coffee bite a dry stout is built around.',
    tags: ['roasted', 'unmalted', 'coffee', 'stout', 'czech'],
    provenance: 'representative',
  },
  {
    id: 'raven-roasted-wheat',
    name: 'Roasted Wheat Malt',
    maker: 'Raven Trading',
    origin: 'Czech Republic',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 70,
    moisturePercent: 4.5,
    colorLovibond: 340.1,
    colorEbc: 900,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Made like the black malt but pulled at a lower roast, and huskless besides. Finer and rounder than black malt at the same color, so it darkens a beer without the scrape.',
    tags: ['roasted', 'wheat', 'dark', 'porter', 'czech'],
    provenance: 'representative',
  },

  // -------------------------------------------------------------------------
  // Tchecomalt — Sladovny Soufflet ČR. Kilned malts from Prostějov, Nymburk,
  // Kroměříž and Hodonice; the caramel and coloured malts all from Litovel,
  // which also keeps the floor malting.
  // -------------------------------------------------------------------------
  {
    id: 'tchecomalt-pilsen',
    name: 'Pilsen Malt',
    maker: 'Tchecomalt',
    origin: 'Czech Republic',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 80.5,
    moisturePercent: 5,
    colorLovibond: 1.8,
    colorEbc: 3.5,
    diastaticPowerLintner: 70,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The volume Czech pilsner malt, from Haná spring barley — Bojos and its relatives — and well enough modified to saccharify inside fifteen minutes. Honeyed and gently herbal where a German pilsner malt is plainer.',
    tags: ['base', 'lager', 'pilsner', 'czech', 'bohemian'],
    provenance: 'representative',
  },
  {
    id: 'tchecomalt-floor-malted-pilsen',
    name: 'Floor Malted Pilsen Malt',
    maker: 'Tchecomalt',
    origin: 'Czech Republic',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 80.5,
    moisturePercent: 5,
    colorLovibond: 1.8,
    colorEbc: 3.5,
    diastaticPowerLintner: 70,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The same barley and the same published analysis as the standard pilsen malt, germinated on the floors at Litovel without forced air. The numbers do not separate the two; the aroma does, and it is the reason a decoction-brewed ležák is worth the premium.',
    tags: ['base', 'lager', 'pilsner', 'czech', 'bohemian', 'floor-malted'],
    provenance: 'representative',
  },
  {
    id: 'tchecomalt-pale-ale',
    name: 'Pale Ale Malt',
    maker: 'Tchecomalt',
    origin: 'Czech Republic',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 80,
    moisturePercent: 5,
    colorLovibond: 2.2,
    colorEbc: 4.5,
    diastaticPowerLintner: 72,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Kilned hotter than the pilsen malt and modified further — a Kolbach index above 42 — so it converts itself easily and mashes in a single infusion. Biscuit and light caramel over a golden to pale amber wort.',
    tags: ['base', 'ale', 'pale-ale', 'czech'],
    provenance: 'representative',
  },
  {
    id: 'tchecomalt-vienna',
    name: 'Vienna Malt',
    maker: 'Tchecomalt',
    origin: 'Czech Republic',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 79,
    moisturePercent: 4.5,
    colorLovibond: 3.7,
    colorEbc: 8.5,
    diastaticPowerLintner: 65,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Two-row spring barley given a modified germination and kiln to build color and flavor together. Lifts a beer a shade towards gold, firms the head, and can be the whole grist of a Vienna lager.',
    tags: ['base', 'kilned', 'vienna', 'lager', 'czech'],
    provenance: 'representative',
  },
  {
    id: 'tchecomalt-munich',
    name: 'Munich Malt',
    maker: 'Tchecomalt',
    origin: 'Czech Republic',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 79,
    moisturePercent: 4.5,
    colorLovibond: 8,
    colorEbc: 20,
    diastaticPowerLintner: 42,
    suggestedMaxPercent: 80,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The darker of the two kilned specialities, and the one that makes itself heard in the flavor rather than only in the color. Country Malt sell it as the foundation of a Czech tmavé; it holds up the same way in a dunkel or a bock.',
    tags: ['base', 'kilned', 'munich', 'dark', 'czech'],
    provenance: 'representative',
  },
  {
    id: 'tchecomalt-wheat',
    name: 'Wheat Malt',
    maker: 'Tchecomalt',
    origin: 'Czech Republic',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 83,
    moisturePercent: 6,
    colorLovibond: 1.9,
    colorEbc: 3.75,
    diastaticPowerLintner: 76,
    suggestedMaxPercent: 70,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Malted from wheat varieties picked for how they store, and the highest-extract row in the Tchecomalt range at a minimum 83 % dry basis. Buys foam stability and the wheat-beer palate; costs runoff speed, so hull it.',
    tags: ['base', 'wheat', 'czech'],
    provenance: 'representative',
  },
  {
    id: 'tchecomalt-pilsen-caramel',
    name: 'Pilsen Caramel',
    maker: 'Tchecomalt',
    origin: 'Czech Republic',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75,
    moisturePercent: 7.5,
    colorLovibond: 11.8,
    colorEbc: 30,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Green malt saccharified in the roasting drum and stopped early — the palest caramel Litovel make. Rounds out a pale lager and nudges its color without announcing itself as a caramel malt.',
    tags: ['crystal', 'caramel', 'light', 'body', 'czech'],
    provenance: 'spec-sheet',
  },
  {
    id: 'tchecomalt-light-caramel',
    name: 'Light Caramel',
    maker: 'Tchecomalt',
    origin: 'Czech Republic',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75,
    moisturePercent: 6,
    colorLovibond: 42,
    colorEbc: 110,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Sweetness and a delicate malt aroma at a color that still reads gold rather than copper. The usual first reach for fullness and foam in a pale or amber lager.',
    tags: ['crystal', 'caramel', 'light', 'head-retention', 'czech'],
    provenance: 'spec-sheet',
  },
  {
    id: 'tchecomalt-caramel',
    name: 'Caramel',
    maker: 'Tchecomalt',
    origin: 'Czech Republic',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 74,
    moisturePercent: 6,
    colorLovibond: 57.1,
    colorEbc: 150,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The middle grade, and the one that brings the copper cast Tchecomalt describe. Smooth caramel sweetness with enough body behind it to hold a polotmavý together.',
    tags: ['crystal', 'caramel', 'amber', 'red', 'czech'],
    provenance: 'spec-sheet',
  },
  {
    id: 'tchecomalt-dark-caramel',
    name: 'Dark Caramel',
    maker: 'Tchecomalt',
    origin: 'Czech Republic',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 74,
    moisturePercent: 5,
    colorLovibond: 75.9,
    colorEbc: 200,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Roasted hotter than the standard caramel for a deeper color and a richer sweetness, and still finished so the foam stays white. Fullness and stability for a dark lager.',
    tags: ['crystal', 'caramel', 'dark', 'malty', 'czech'],
    provenance: 'spec-sheet',
  },
  {
    id: 'tchecomalt-extra-dark-caramel',
    name: 'Extra Dark Caramel',
    maker: 'Tchecomalt',
    origin: 'Czech Republic',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 73,
    moisturePercent: 4.5,
    colorLovibond: 151.4,
    colorEbc: 400,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The darkest of the caramels, where the sweetness starts to carry dried fruit and burnt sugar. A few percent is the whole of its job in a tmavé, a bock or a porter.',
    tags: ['crystal', 'caramel', 'dark', 'porter', 'czech'],
    provenance: 'spec-sheet',
  },
  {
    id: 'tchecomalt-chocolate',
    name: 'Chocolate Malt',
    maker: 'Tchecomalt',
    origin: 'Czech Republic',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 73,
    moisturePercent: 4.5,
    colorLovibond: 340.1,
    colorEbc: 900,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Dekilned malt taken through a controlled roast to color rather than to char. Cocoa and dark bread crust, and Litovel stop it short of the point where the roast starts tinting the head.',
    tags: ['roasted', 'chocolate', 'dark', 'porter', 'stout', 'czech'],
    provenance: 'spec-sheet',
  },
  {
    id: 'tchecomalt-coloured',
    name: 'Coloured Malt',
    maker: 'Tchecomalt',
    origin: 'Czech Republic',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 70,
    moisturePercent: 4.5,
    colorLovibond: 509.9,
    colorEbc: 1350,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 5,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      "The darkest thing Tchecomalt roast, sold to set color rather than flavor — the Czech brewer's equivalent of a debittered black malt. A percent or two turns a beer black and leaves the foam alone.",
    tags: ['roasted', 'black', 'dark', 'stout', 'czech'],
    provenance: 'spec-sheet',
  },
];
